import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  DoCheck,
} from "@angular/core";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from "@angular/forms";

export interface DateRangeValidationConfig {
  type: 'start' | 'end';
  otherDate?: string;
  currentTime?: string;
  otherTime?: string;
}

@Component({
  selector: "app-date-input",
  templateUrl: "./date-input.component.html",
  styleUrls: ["./date-input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    },
  ],
})
export class DateInputComponent implements ControlValueAccessor, OnChanges, DoCheck {
  @Input() label: string;
  @Input() placeholder = "dd-mm-aaaa";
  @Input() name: string;
  @Input() showIcon = true;
  @Input() min: string;
  @Input() max: string;

  @Input() showTime = false;
  @Input() timeLabel = "Horário";
  @Input() timeName: string;
  @Input() timePlaceholder = "00:00";
  @Input() timeDisabled = false;
  @Input() time: string;

  @Input() dateRangeValidation: DateRangeValidationConfig;

  @ViewChild("dateInput", { static: false }) dateInput: ElementRef<HTMLInputElement>;

  @Output() dateChange = new EventEmitter<string>();
  @Output() timeChange = new EventEmitter<string>();

  value: string;
  disabled = false;
  validationError = '';
  private calculatedMin: string;
  private calculatedMax: string;
  private lastValidationConfig: DateRangeValidationConfig;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dateRangeValidation'] || changes['value'] || changes['time']) {
      this.validateDateRange();
    }
  }

  ngDoCheck(): void {
    if (this.dateRangeValidation) {
      const currentConfig = this.dateRangeValidation;
      const lastConfig = this.lastValidationConfig;
      
      if (!lastConfig || 
          lastConfig.otherDate !== currentConfig.otherDate ||
          lastConfig.otherTime !== currentConfig.otherTime ||
          lastConfig.currentTime !== currentConfig.currentTime ||
          lastConfig.type !== currentConfig.type) {
        this.lastValidationConfig = { ...currentConfig };
        this.validateDateRange();
      }
    } else if (this.lastValidationConfig) {
      this.lastValidationConfig = null;
      this.validateDateRange();
    }
  }

  onInput(value: string): void {
    this.value = value;
    this.validateDateRange();
    this.onChange(value);
    this.dateChange.emit(value);
  }

  onTimeInput(value: string): void {
    this.time = value;
    this.validateDateRange();
    this.timeChange.emit(value);
  }

  private validateDateRange(): void {
    if (!this.dateRangeValidation) {
      this.validationError = '';
      this.updateMinMax();
      return;
    }

    const { type, otherDate, currentTime, otherTime } = this.dateRangeValidation;
    const actualCurrentTime = currentTime || this.time;
    this.validationError = '';

    if (actualCurrentTime) {
      if (!this.validateMilitaryTime(actualCurrentTime)) {
        this.validationError = 'O horário deve seguir o formato (00:00 a 23:59)';
        return;
      }
    }

    if (this.value && otherDate) {
      const currentDate = new Date(this.value);
      const otherDateObj = new Date(otherDate);

      if (type === 'start') {
        if (currentDate > otherDateObj) {
          this.validationError = 'A data de início não pode ser maior que a data de fim';
          this.updateMinMax();
          return;
        }
      } else if (type === 'end') {
        if (currentDate < otherDateObj) {
          this.validationError = 'A data de fim não pode ser menor que a data de início';
          this.updateMinMax();
          return;
        }
      }

      if (actualCurrentTime && otherTime) {
        if (!this.validateTimeRange(actualCurrentTime, otherTime, type)) {
          return;
        }
      }
    }

    this.updateMinMax();
  }

  private validateMilitaryTime(time: string): boolean {
    if (!time || time.length !== 5) {
      return false;
    }

    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  }

  private validateTimeRange(currentTime: string, otherTime: string, type: 'start' | 'end'): boolean {
    if (!this.validateMilitaryTime(currentTime) || !this.validateMilitaryTime(otherTime)) {
      return true;
    }

    const [currentHours, currentMinutes] = currentTime.split(':').map(Number);
    const [otherHours, otherMinutes] = otherTime.split(':').map(Number);

    const currentTotalMinutes = currentHours * 60 + currentMinutes;
    const otherTotalMinutes = otherHours * 60 + otherMinutes;

    if (type === 'start') {
      if (currentTotalMinutes >= otherTotalMinutes) {
        this.validationError = 'O horário de início deve ser menor que o horário de fim';
        return false;
      }
    } else if (type === 'end') {
      if (currentTotalMinutes <= otherTotalMinutes) {
        this.validationError = 'O horário de fim deve ser maior que o horário de início';
        return false;
      }
    }

    return true;
  }

  private updateMinMax(): void {
    if (!this.dateRangeValidation || !this.dateRangeValidation.otherDate) {
      this.calculatedMin = null;
      this.calculatedMax = null;
      return;
    }

    const { type, otherDate } = this.dateRangeValidation;

    if (type === 'start') {
      this.calculatedMax = otherDate;
      this.calculatedMin = null;
    } else if (type === 'end') {
      this.calculatedMin = otherDate;
      this.calculatedMax = null;
    }
  }

  get effectiveMin(): string {
    return this.calculatedMin || this.min;
  }

  get effectiveMax(): string {
    return this.calculatedMax || this.max;
  }

  onBlur(): void {
    this.onTouched();
  }

  openCalendar(): void {
    if (this.dateInput?.nativeElement && !this.disabled) {
      this.dateInput.nativeElement.showPicker();
    }
  }
}

